import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Loading } from 'components/Loading';
import { withFormikField } from 'utils/formik';
import { isMainLoading } from 'redux/modules/shipper-order-form/selectors';
import { clearForm as clearFormAction } from 'redux/modules/shipper-order-form/effects';

import {
  baseShipperOrder,
  COLLECTION,
  DISTRIBUTION,
  EXTRAS_FIELD,
  HANDLING_EXTRAS,
  SHIPMENTS_FIELD,
  SHIPPER_ORDER_TYPE,
  TRIGGER_FORM_CHANGE,
} from 'modules/shipper-order/Form/constants';

import { Button, Flexer, SegmentedButton } from '@ontruck/orc';

import { LoadingWrapper } from './FormModalHeader.styled';
import { IS_TENANT } from 'tenant/config';
import { injectIntl } from 'react-intl';

const FormModalHeader = ({
  mainLoading,
  showCancelButton,
  showClearButton,
  isNew,
  typeSelectorDisabled,
  setFieldValue,
  resetForm,
  values,
  clearForm,
  onCancelClick,
  intl,
}) => {
  const { shipper_order_type = '' } = values;
  const labels = intl.messages.modules.shipper_order.form.header;
  const t = {
    new: labels.new,
    edit: labels.edit,
    cancel_order: labels.cancel_order,
    clear_order: labels.clear_order,
    types: {
      [DISTRIBUTION]: IS_TENANT ? labels.import : labels.distribution_label,
      [COLLECTION]: IS_TENANT ? labels.export : labels.collection_label,
    },
  };

  const removeHandlingExtras = (extras) =>
    extras?.filter((extra) => !HANDLING_EXTRAS.includes(extra)) || [];

  const onOrderTypeChange = (value) => {
    if (IS_TENANT) {
      clearForm();
      resetForm(baseShipperOrder());
    }
    setFieldValue(SHIPPER_ORDER_TYPE, value);

    // Remove handling extras globally and for each shipment to prevent conflicts
    setFieldValue(EXTRAS_FIELD, removeHandlingExtras(values?.[EXTRAS_FIELD]));
    values?.shipments?.forEach((shipment, i) => {
      setFieldValue(
        [SHIPMENTS_FIELD, i, EXTRAS_FIELD],
        removeHandlingExtras(shipment?.[EXTRAS_FIELD])
      );
    });
  };

  const firstMultiroute = IS_TENANT ? COLLECTION : DISTRIBUTION;
  const secondMultiroute = IS_TENANT ? DISTRIBUTION : COLLECTION;
  return (
    <Flexer.Parent
      alignItems="center"
      justifyContent="space-between"
      data-testid="form-modal-header"
    >
      <Flexer.Parent alignItems="center">
        <div>{(isNew && t.new) || t.edit}</div>
        <LoadingWrapper>
          <Loading size={25} />
        </LoadingWrapper>
      </Flexer.Parent>
      {!mainLoading && (
        <Fragment>
          <Flexer.Child>
            <SegmentedButton.Parent
              size="M"
              value={shipper_order_type || firstMultiroute}
              onChange={(_e, { value }) => onOrderTypeChange(value)}
              disabled={typeSelectorDisabled}
            >
              <SegmentedButton.Child value={firstMultiroute} label={t.types[firstMultiroute]} />
              <SegmentedButton.Child value={secondMultiroute} label={t.types[secondMultiroute]} />
            </SegmentedButton.Parent>
          </Flexer.Child>
          <Flexer.Child>
            {showCancelButton && (
              <Button
                skin="cancel"
                text={t.cancel_order}
                onClick={onCancelClick}
                dataE2E="shipperOrder_edit__cancel"
              />
            )}
            {showClearButton && (
              <Button
                skin="secondary"
                nude
                text={t.clear_order}
                onClick={() => {
                  clearForm();
                  resetForm(baseShipperOrder());
                  setFieldValue(TRIGGER_FORM_CHANGE, true);
                }}
              />
            )}
          </Flexer.Child>
        </Fragment>
      )}
    </Flexer.Parent>
  );
};

FormModalHeader.propTypes = {
  mainLoading: PropTypes.bool.isRequired,
  initialShipperOrder: PropTypes.object.isRequired,
  showCancelButton: PropTypes.bool,
  showClearButton: PropTypes.bool,
  isNew: PropTypes.bool,
  typeSelectorDisabled: PropTypes.bool,
  setFieldValue: PropTypes.func,
  resetForm: PropTypes.func,
  values: PropTypes.object,
  clearForm: PropTypes.func,
  onCancelClick: PropTypes.func.isRequired,
};

FormModalHeader.defaultProps = {
  showCancelButton: true,
  isNew: false,
  showClearButton: false,
  typeSelectorDisabled: true,
};

export default connect(
  (state) => ({
    mainLoading: isMainLoading(state),
  }),
  {
    clearForm: clearFormAction,
  }
)(withFormikField(injectIntl(FormModalHeader)));
